/**
 * Brand variable interpolation utilities for runtime/client usage.
 * Server-side consumers should import `interpolation.server.ts`.
 */
import {
	buildBrandInterpolationVariables,
	getRuntimeBrandInterpolationConfig,
} from '#app/utils/pz/brand/runtimeBrandConfig'

export function interpolateBrandVariables<T extends Record<string, any>>(
	translations: T,
	options?: { language?: string },
): T {
	const language = options?.language?.toLowerCase() ?? 'en'
	const brand = getRuntimeBrandInterpolationConfig()
	const vars = buildBrandInterpolationVariables(brand, language)
	return recursiveInterpolate(translations, vars) as T
}

export function interpolateBrandString(
	input: string,
	options?: { language?: string; brandKey?: string | null },
): string {
	const language = options?.language?.toLowerCase() ?? 'en'
	const brand = getRuntimeBrandInterpolationConfig(options?.brandKey ?? null)
	return interpolateString(
		input,
		buildBrandInterpolationVariables(brand, language),
	)
}

function recursiveInterpolate(obj: any, vars: Record<string, string>): any {
	if (typeof obj === 'string') {
		return interpolateString(obj, vars)
	}

	if (Array.isArray(obj)) {
		return obj.map((item) => recursiveInterpolate(item, vars))
	}

	if (obj !== null && typeof obj === 'object') {
		const result: Record<string, any> = {}
		for (const [key, value] of Object.entries(obj)) {
			result[key] = recursiveInterpolate(value, vars)
		}
		return result
	}

	return obj
}

function interpolateString(str: string, vars: Record<string, string>): string {
	return str.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
		return vars[varName] ?? match
	})
}
